// V4.12 Phase E — "Save this search as an alert" dialog.
// Shows the CANONICAL criteria (same vocabulary as Heatmap/AI), lets
// the user pick name + frequency, validates against master data via
// POST /saved-searches/preview (no alerts on fuzzy/unresolved terms).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Bell, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  createSavedSearch,
  previewSavedSearch,
} from "../../api/savedSearchesApi";
import { enumLabel, featureLabel } from "../../utils/enumLabels";

const cap = (s) =>
  String(s).split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
const fmtNum = (v) => Number(v).toLocaleString("de-AT");

export function criteriaChips(vertical, filters, t) {
  const f = filters || {};
  const chips = [];
  const push = (key, label) => chips.push({ key, label });
  if (vertical === "immo" && f.transaction_type) {
    push("tt", t(`chips.${f.transaction_type === "rent" ? "rent" : "buy"}`, { ns: "heatmap" }));
  }
  (f.makes || []).forEach((m) => push(`make-${m}`, cap(m)));
  (f.models || []).forEach((m) => push(`model-${m}`, m));
  if (f.year_min || f.year_max) {
    push("year", `${t("chips.year", { ns: "heatmap" })} ${f.year_min || "…"}–${f.year_max || "…"}`);
  }
  if (f.km_max) push("km", `≤ ${fmtNum(f.km_max)} km`);
  if (f.price_min || f.price_max) {
    push("price", `${f.price_min ? fmtNum(f.price_min) : "…"}–${f.price_max ? fmtNum(f.price_max) : "…"} €`);
  }
  (f.fuels || []).forEach((v) => push(`fuel-${v}`, enumLabel("fuel", v)));
  (f.gearboxes || []).forEach((v) => push(`gb-${v}`, enumLabel("gearbox", v)));
  (f.body_types || []).forEach((v) => push(`bt-${v}`, enumLabel("body_type", v)));
  (f.seller_types || []).forEach((v) => push(`st-${v}`, enumLabel("seller_type", v)));
  (f.object_types || []).forEach((v) => push(`ot-${v}`, enumLabel("object_type", v)));
  if (f.area_min || f.area_max) push("area", `${f.area_min || "…"}–${f.area_max || "…"} m²`);
  if (f.rooms_min) push("rooms", `${f.rooms_min}+ ${t("chips.rooms", { ns: "heatmap" })}`);
  if (f.yield_min) push("yield", `≥ ${f.yield_min} % ${t("chips.yield", { ns: "heatmap" })}`);
  (f.features || []).forEach((v) => push(`feat-${v}`, featureLabel(v)));
  if (f.region) push("region", `${t("alerts.region")}: ${f.region}`);
  return chips;
}

export const SaveAlertDialog = ({
  open,
  onOpenChange,
  vertical,
  filters,
  bbox,
  label,
  source,
}) => {
  const { t } = useTranslation("common");
  const [name, setName] = useState("");
  const [frequency, setFrequency] = useState("daily");
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(label || "");
    setErrors([]);
    setPreview(null);
    setLoading(true);
    previewSavedSearch({ vertical, filters, bbox })
      .then((d) => {
        setErrors(d.errors || []);
        setPreview(d.preview);
      })
      .catch(() => setErrors([{ field: "filters", code: "preview_failed" }]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chips = criteriaChips(vertical, filters, t);
  const blocked = errors.length > 0;

  const submit = async () => {
    if (!name.trim()) {
      toast.error(t("alerts.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      const res = await createSavedSearch({
        vertical, filters, bbox, label, source,
        name: name.trim(), frequency,
      });
      toast.success(res.updated ? t("alerts.updated") : t("alerts.saved"));
      onOpenChange(false);
    } catch (e) {
      const details = e?.response?.data?.error?.details;
      if (details?.errors) setErrors(details.errors);
      toast.error(t("alerts.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="save-alert-dialog"
        className="max-w-md border-zinc-800 bg-zinc-950 text-zinc-100"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4 text-amber-400" />
            {t("alerts.dialogTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              {t("alerts.criteria")} · {t(vertical === "auto" ? "alerts.vertAuto" : "alerts.vertImmo")}
              {" · "}
              {bbox ? t("alerts.mapRegion") : filters?.region ? filters.region : t("alerts.wholeEurope")}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="save-alert-criteria">
              {chips.length ? (
                chips.map((c) => (
                  <span
                    key={c.key}
                    className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-200"
                  >
                    {c.label}
                  </span>
                ))
              ) : (
                <span className="text-xs text-zinc-500">—</span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> …
            </div>
          ) : blocked ? (
            <div
              data-testid="save-alert-errors"
              className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300"
            >
              <div className="mb-1 font-semibold">{t("alerts.unresolved")}</div>
              <ul className="list-inside list-disc space-y-0.5">
                {errors.map((e, i) => (
                  <li key={i}>
                    {t(`alerts.err_${e.code}`, { defaultValue: e.code })}
                    {e.value ? `: „${e.value}“` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div data-testid="save-alert-match-count" className="text-xs text-emerald-300">
              {t("alerts.matchesNow", { count: preview?.total ?? 0 })}
            </div>
          )}

          <div>
            <label className="mb-1 block text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              {t("alerts.nameLabel")}
            </label>
            <input
              data-testid="alert-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("alerts.namePlaceholder")}
              maxLength={80}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-500/50"
            />
          </div>

          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
              {t("alerts.frequency")}
            </div>
            <div className="flex gap-2">
              {["daily", "weekly"].map((f) => (
                <button
                  key={f}
                  type="button"
                  data-testid={`alert-frequency-${f}`}
                  onClick={() => setFrequency(f)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    frequency === f
                      ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                      : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {t(`alerts.${f}`)}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            data-testid="save-alert-submit"
            disabled={saving || loading || blocked}
            onClick={submit}
            className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-zinc-950 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? t("alerts.saving") : t("alerts.save")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
